import { gsap } from 'gsap';
import SplitText from '../../../../../global/js/vendors/gsap-splittext/splittext';

gsap.registerPlugin(SplitText);

/**
 * Animates the hero section using GSAP.
 *
 * This function applies animations to various elements within the hero section,
 * ensuring a smooth, visually engaging transition.
 *
 * @function heroAuto2
 * @returns {void}
 */
function heroAuto2() {
  /**
   * Retrieves the hero type from the data attribute.
   *
   * @constant {string}
   */
  const heroType = $('*[data-hero-type]').data('hero-type');

  /**
   * Stores references to key elements in the hero section.
   *
   * @constant {Object.<string, JQuery<HTMLElement> | HTMLElement[]>}
   */
  const elements = {
    taxonomy: $(`.o-hero-${heroType}__taxonomy`),
    title: $(`.o-hero-${heroType} h1.a-title`),
    subTitle: $(`.o-hero-${heroType}__subtitle-wrapper .a-text`),
    boardRows: gsap.utils.toArray(`.o-hero-${heroType}__board-row`),
    sideImageWrapper: $(`.o-hero-${heroType}__column--2`),
    sideImage: $(`.o-hero-${heroType}__img-wrapper .a-img`),
    sideImageMobile: $(`.o-hero-${heroType}__img-wrapper--mobile .a-img`),
    sideImageCover: $(
      `.o-hero-${heroType}__img-wrapper ~ .o-hero-${heroType}__media-cover`,
    ),
    sideImageMobileCover: $(
      `.o-hero-${heroType}__img-wrapper--mobile ~ .o-hero-${heroType}__media-cover`,
    ),
    sideVideo: $(`.o-hero-${heroType}__video-wrapper .a-video`),
    sideVideoMobile: $(`.o-hero-${heroType}__video-wrapper--mobile .a-video`),
    sideVideoCover: $(
      `.o-hero-${heroType}__video-wrapper ~ .o-hero-${heroType}__media-cover`,
    ),
    sideVideoMobileCover: $(
      `.o-hero-${heroType}__video-wrapper--mobile ~ .o-hero-${heroType}__media-cover`,
    ),
  };

  /**
   * Initializes a GSAP timeline for sequencing animations.
   *
   * @constant {gsap.core.Timeline}
   */
  const tl = gsap.timeline();

  // ==========================
  // Animate Side Image & Cover
  // ==========================
  /*if (elements.sideImage.length) {
    tl.fromTo(
      elements.sideImageWrapper,
      { opacity: 0 },
      { duration: 1, opacity: 1, ease: 'power4.inOut' },
      '<',
    )
      .fromTo(
        elements.sideImage,
        { scale: 1.2 },
        { duration: 1.2, scale: 1, ease: 'power4.inOut' },
        '<',
      )
      .to(
        elements.sideImageCover,
        { duration: 1.2, yPercent: 100, ease: 'power4.inOut' },
        '<',
      );
  }*/

  // ==========================
  // Animate Side Video & Cover
  // ==========================
  /*if (elements.sideVideo.length) {
    tl.fromTo(
      elements.sideImageWrapper,
      { opacity: 0 },
      { duration: 1, opacity: 1, ease: 'power4.inOut' },
      '<',
    )
      .fromTo(
        elements.sideVideo,
        { scale: 1.2 },
        { duration: 1.2, scale: 1, ease: 'power4.inOut' },
        '<',
      )
      .to(
        elements.sideVideoCover,
        { duration: 1.2, yPercent: 100, ease: 'power4.inOut' },
        '<',
      );
  }*/

  if (elements.sideImageWrapper.length) {
    tl.fromTo(
      elements.sideImageWrapper,
      { opacity: 0 },
      { duration: 1, opacity: 1, ease: 'power4.inOut' },
      '<',
    )
  }

  // ==========================
  // Animate Taxonomy (Breadcrumbs)
  // ==========================
  if (elements.taxonomy.length) {
    tl.fromTo(
      elements.taxonomy,
      { opacity: 0 },
      { duration: 1, opacity: 1, ease: 'power4.inOut' },
      '<',
    );
  }

  // ==========================
  // Animate Title with SplitText
  // ==========================
  if (elements.title.length) {
    tl.fromTo(
      elements.title,
      { autoAlpha: 0 },
      { duration: 0.3, autoAlpha: 1, ease: 'power4.inOut' },
      '<',
    );

    /**
     * Splits the title into lines and words for animation.
     *
     * @constant {SplitText}
     */
    const split = new SplitText(elements.title, {
      tag: 'span',
      type: 'lines,words',
      linesClass: 'overflow-hidden',
    });

    tl.from(
      split.words,
      {
        duration: 0.8,
        yPercent: 100,
        autoAlpha: 0,
        ease: 'power4.inOut',
        stagger: 0.1,
      },
      '<',
    );
  }

  // ==========================
  // Animate Subtitle with SplitText
  // ==========================
  if (elements.subTitle.length) {
    tl.fromTo(
      elements.subTitle,
      { autoAlpha: 0 },
      { duration: 0.3, autoAlpha: 1, ease: 'power4.inOut' },
      '<',
    );

    /**
     * Splits the subtitle into lines and words for animation.
     *
     * @constant {SplitText}
     */
    const split = new SplitText(elements.subTitle, {
      tag: 'span',
      type: 'lines,words',
      linesClass: 'overflow-hidden',
    });

    tl.from(
      split.words,
      {
        duration: 0.8,
        yPercent: 100,
        autoAlpha: 0,
        ease: 'power4.inOut',
        stagger: 0.1,
      },
      '<',
    );
  }

  // ==========================
  // Animate Board Rows with Stagger
  // ==========================
  if (elements.boardRows.length) {
    elements.boardRows.forEach((row, index) => {
      /**
       * Retrieves all separator lines within the row.
       *
       * @constant {HTMLElement[]}
       */
      const separators = gsap.utils.toArray(
        row.querySelectorAll(`.o-hero-${heroType}__separator-line`),
      );

      // Animate the row with staggered opacity transition.
      tl.fromTo(
        row,
        { opacity: 0 },
        { duration: 1, opacity: 1, ease: 'power4.inOut' },
        `<=${index * 0.1}`,
      );

      // Animate separator lines for each row
      if (separators.length) {
        tl.fromTo(
          separators,
          { scaleX: 0 },
          {
            duration: 1,
            scaleX: 1,
            ease: 'power4.inOut',
            stagger: 0.1,
          },
          '<', // Keeps animation relative to previous animation
        );

        /*if (elements.sideImageMobile.length) {
          tl.fromTo(
            elements.sideImageMobile,
            { scale: 1.2 },
            { duration: 1.2, scale: 1, ease: 'power4.inOut' },
            '<-=0.3',
          ).to(
            elements.sideImageMobileCover,
            { duration: 1.2, yPercent: 100, ease: 'power4.inOut' },
            '<',
          );
        }*/

        /*if (elements.sideVideoMobile.length) {
          tl.fromTo(
            elements.sideVideoMobile,
            { scale: 1.2 },
            { duration: 1.2, scale: 1, ease: 'power4.inOut' },
            '<-=0.3',
          ).to(
            elements.sideVideoMobileCover,
            { duration: 1.2, yPercent: 100, ease: 'power4.inOut' },
            '<',
          );
        }*/
      }
    });
  }
}

export default heroAuto2;
